import { useState, useEffect } from 'react';
import { db } from '../db';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Crown, Gift, Download, Trash2, Plus, Lock, Palette, LogIn, LogOut, Copy, Share2, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import PaywallModal from '../components/PaywallModal';
import OnboardingModal from '../components/OnboardingModal';
import ResetTripModal from '../components/ResetTripModal';
import ThemeSelector from '../components/ThemeSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsScreen = ({ isPremium, setIsPremium }) => {
  const [dailyBudget, setDailyBudget] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [referralCode, setReferralCode] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [showPaywall, setShowPaywall] = useState(false);
  const [showNewBudget, setShowNewBudget] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [currentTripInfo, setCurrentTripInfo] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [adminTaps, setAdminTaps] = useState(0);
  const [showAdminInput, setShowAdminInput] = useState(false);
  const [adminCode, setAdminCode] = useState('');

  useEffect(() => {
    loadSettings();
    loadCurrentTrip();
    checkAuth();
  }, []);

  const loadCurrentTrip = async () => {
    try {
      const totalBudget = await db.settings.get('total_budget');
      const tripDuration = await db.settings.get('trip_duration');
      const destination = await db.settings.get('destination');
      const tripStart = await db.settings.get('trip_start_date');

      if (totalBudget && tripDuration && destination) {
        setCurrentTripInfo({
          totalBudget: totalBudget.value,
          tripDuration: tripDuration.value,
          destination: destination.value,
          startDate: tripStart?.value
        });
      }
    } catch (error) {
      console.error('Failed to load trip info:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const budgetSetting = await db.settings.get('daily_budget');
      const currencySetting = await db.settings.get('currency');
      const referralSetting = await db.settings.get('referral_code');

      if (budgetSetting) setDailyBudget(budgetSetting.value);
      if (currencySetting) setCurrency(currencySetting.value);
      if (referralSetting) setReferralCode(referralSetting.value);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const checkAuth = async () => {
    // Skip if returning from OAuth
    if (window.location.hash?.includes('session_id=')) return;
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const user = await res.json();
        setAuthUser(user);
        loadReferralStats();
      }
    } catch (e) {
      // Not signed in — that's fine, offline mode
    }
  };

  const loadReferralStats = async () => {
    try {
      const res = await fetch(`${API}/referral/stats`, { credentials: 'include' });
      if (res.ok) {
        setReferralStats(await res.json());
      }
    } catch (e) {
      console.error('Failed to load referral stats:', e);
    }
  };

  const handleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignOut = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
      setAuthUser(null);
      setReferralStats(null);
      toast.success('Signed out');
    } catch (e) {
      toast.error('Failed to sign out');
    }
  };

  const copyReferralCode = async () => {
    const code = authUser?.referral_code || referralCode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      toast.success('Code copied!');
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (e) {
      toast.error('Failed to copy');
    }
  };

  const shareReferralCode = async () => {
    const code = authUser?.referral_code || referralCode;
    if (!code) return;
    const text = `Use my referral code ${code} to sign up for Wanderer's Wallet and we both get a free month of Premium!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Wanderer's Wallet", text });
      } catch (e) { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success('Share text copied to clipboard!');
    }
  };

  const saveDailyBudget = async () => {
    if (!dailyBudget || dailyBudget <= 0) {
      toast.error('Please enter a valid budget');
      return;
    }
    
    try {
      await db.settings.put({ key: 'daily_budget', value: parseFloat(dailyBudget).toString() });
      toast.success('Daily budget updated');
      
      // Force reload to reflect changes
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Failed to save budget:', error);
      toast.error('Failed to save budget');
    }
  };

  const saveCurrency = async (newCurrency) => {
    try {
      setCurrency(newCurrency);
      await db.settings.put({ key: 'currency', value: newCurrency });
      toast.success('Currency updated');
      
      // Force reload to reflect changes
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error('Failed to save currency:', error);
      toast.error('Failed to save currency');
    }
  };

  const generateReferralCode = async () => {
    try {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      
      // Store the current code
      await db.settings.put({ key: 'referral_code', value: code });
      setReferralCode(code);
      
      // Also track all generated codes to prevent self-referral loopholes
      const generatedCodes = await db.settings.get('generated_referral_codes');
      let codeList = [];
      if (generatedCodes && generatedCodes.value) {
        codeList = JSON.parse(generatedCodes.value);
      }
      codeList.push(code);
      await db.settings.put({ key: 'generated_referral_codes', value: JSON.stringify(codeList) });
      
      toast.success('Referral code generated');
    } catch (error) {
      toast.error('Failed to generate code');
    }
  };

  const redeemReferral = async () => {
    if (!redeemCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    const codeToRedeem = redeemCode.trim().toUpperCase();
    
    // Master test codes - always work for testing
    const MASTER_CODES = ['WANDERER', 'PREMIUM', 'TESTPRO', 'DEVMODE', 'MASTER-PREMIUM-2026'];
    
    if (MASTER_CODES.includes(codeToRedeem)) {
      try {
        await db.settings.put({ key: 'is_premium', value: true });
        const premiumUntil = new Date();
        premiumUntil.setFullYear(premiumUntil.getFullYear() + 10);
        await db.settings.put({ key: 'premium_until', value: premiumUntil.toISOString() });
        
        setIsPremium(true);
        toast.success('Premium unlocked! All features activated.');
        setRedeemCode('');
        return;
      } catch (error) {
        toast.error('Failed to activate premium');
        return;
      }
    }

    // If signed in, use server-side referral
    if (authUser) {
      try {
        const res = await fetch(`${API}/referral/apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ referral_code: codeToRedeem })
        });
        const data = await res.json();
        if (res.ok) {
          toast.success(data.message || 'Referral code applied!');
          setRedeemCode('');
          loadReferralStats();
        } else {
          toast.error(data.detail || 'Invalid code');
        }
        return;
      } catch (e) {
        // Fall through to local if server fails
      }
    }
    
    // Local-only fallback (offline mode)
    if (referralCode && codeToRedeem === referralCode.toUpperCase()) {
      toast.error("You can't use your own referral code");
      return;
    }

    try {
      const generatedCodes = await db.settings.get('generated_referral_codes');
      if (generatedCodes && generatedCodes.value) {
        const codeList = JSON.parse(generatedCodes.value);
        if (codeList.includes(codeToRedeem)) {
          toast.error("This is one of your own referral codes!");
          return;
        }
      }
    } catch (e) {}

    try {
      const redeemedCodes = await db.settings.get('redeemed_referral_codes');
      if (redeemedCodes && redeemedCodes.value) {
        const codeList = JSON.parse(redeemedCodes.value);
        if (codeList.includes(codeToRedeem)) {
          toast.error("You've already redeemed this code!");
          return;
        }
      }
    } catch (e) {}

    try {
      const redeemedCodes = await db.settings.get('redeemed_referral_codes');
      let codeList = [];
      if (redeemedCodes && redeemedCodes.value) {
        codeList = JSON.parse(redeemedCodes.value);
      }
      codeList.push(codeToRedeem);
      await db.settings.put({ key: 'redeemed_referral_codes', value: JSON.stringify(codeList) });

      await db.settings.put({ key: 'is_premium', value: true });
      const premiumUntil = new Date();
      premiumUntil.setDate(premiumUntil.getDate() + 14);
      await db.settings.put({ key: 'premium_until', value: premiumUntil.toISOString() });
      
      setIsPremium(true);
      toast.success('2 weeks of premium activated!');
      setRedeemCode('');
    } catch (error) {
      toast.error('Failed to redeem code');
    }
  };

  const exportData = async () => {
    try {
      const expenses = await db.expenses.toArray();
      const dataStr = JSON.stringify(expenses, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wanderers-wallet-export-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      toast.success('Data exported');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const deleteAllData = async () => {
    if (window.confirm('Are you sure? This will delete ALL your data. This cannot be undone.')) {
      try {
        await db.expenses.clear();
        await db.trips.clear();
        await db.settings.clear();
        await db.syncQueue.clear();
        toast.success('All data deleted');
        window.location.reload();
      } catch (error) {
        toast.error('Failed to delete data');
      }
    }
  };

  const resetTrip = async () => {
    try {
      // Clear only trip-related settings
      await db.settings.delete('onboarding_complete');
      await db.settings.delete('total_budget');
      await db.settings.delete('trip_duration');
      await db.settings.delete('daily_budget');
      await db.settings.delete('destination');
      await db.settings.delete('trip_start_date');
      
      setShowResetModal(false);
      toast.success('Trip reset - restarting...');
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Failed to reset trip:', error);
      toast.error('Failed to reset trip');
      setShowResetModal(false);
    }
  };

  const handleCreateNewBudget = () => {
    if (!isPremium) {
      setShowPaywall(true);
      return;
    }
    
    // Archive current trip data before starting new budget
    archiveCurrentTrip();
  };

  const archiveCurrentTrip = async () => {
    try {
      // Generate a unique trip ID for the current trip
      const currentTripId = `trip_${Date.now()}`;
      
      // Save current trip to trips collection with ID
      const tripData = {
        trip_id: currentTripId,
        ...currentTripInfo,
        archived_date: new Date().toISOString(),
        expenses_count: await db.expenses.count()
      };
      
      await db.trips.add(tripData);
      
      // Mark ALL current expenses as belonging to this archived trip
      const allExpenses = await db.expenses.toArray();
      
      for (const expense of allExpenses) {
        await db.expenses.update(expense.id, {
          trip_id: currentTripId,
          archived: true
        });
      }
      
      console.log(`Archived ${allExpenses.length} expenses to trip ${currentTripId}`);
      
      // Clear current trip settings
      await db.settings.delete('onboarding_complete');
      await db.settings.delete('total_budget');
      await db.settings.delete('trip_duration');
      await db.settings.delete('daily_budget');
      await db.settings.delete('destination');
      await db.settings.delete('trip_start_date');
      await db.settings.delete('current_trip_id');
      
      toast.success('Current trip archived! Starting new budget...');
      setShowNewBudget(true);
    } catch (error) {
      console.error('Failed to archive trip:', error);
      toast.error('Failed to create new budget');
    }
  };

  const handleNewBudgetComplete = async (data) => {
    try {
      // Generate new trip ID
      const newTripId = `trip_${Date.now()}`;
      
      // Save new trip data
      await db.settings.put({ key: 'onboarding_complete', value: true });
      await db.settings.put({ key: 'total_budget', value: data.totalBudget.toString() });
      await db.settings.put({ key: 'trip_duration', value: data.tripDuration.toString() });
      await db.settings.put({ key: 'daily_budget', value: data.dailyBudget.toString() });
      await db.settings.put({ key: 'currency', value: data.currency });
      await db.settings.put({ key: 'destination', value: data.destination });
      await db.settings.put({ key: 'trip_start_date', value: new Date().toISOString() });
      await db.settings.put({ key: 'current_trip_id', value: newTripId });

      toast.success('New trip budget created! Today screen is fresh.');
      setShowNewBudget(false);
      
      // Reload to show new budget with clean Today screen
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Failed to save new budget:', error);
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className="p-4 space-y-6 animate-in pb-24">
      {/* Header */}
      <div className="pt-6">
        <h1 className="text-3xl font-heading font-bold text-foreground tracking-tight">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage your preferences</p>
      </div>

      {/* Premium Status */}
      <Card data-testid="premium-status-card" className="bg-gradient-to-br from-card to-secondary/50 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" />
            {isPremium ? 'Premium Active' : 'Free Plan'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!isPremium && (
            <Button
              data-testid="upgrade-to-premium-button"
              onClick={() => setShowPaywall(true)}
              className="w-full bg-primary text-primary-foreground rounded-full"
            >
              Upgrade to Premium
            </Button>
          )}
          {isPremium && (
            <p className="text-muted-foreground">You have access to all premium features</p>
          )}
        </CardContent>
      </Card>

      {/* Current Trip Info */}
      {currentTripInfo && (
        <Card data-testid="current-trip-card" className="bg-card border-border">
          <CardHeader>
            <CardTitle>Current Trip</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Destination:</span>
              <span className="font-semibold">{currentTripInfo.destination}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total Budget:</span>
              <span className="font-semibold">${parseFloat(currentTripInfo.totalBudget).toFixed(0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Duration:</span>
              <span className="font-semibold">{currentTripInfo.tripDuration} days</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create New Budget - Premium Only */}
      <Card data-testid="new-budget-card" className={`bg-card border-border ${!isPremium ? 'opacity-90' : ''}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {!isPremium && <Lock className="w-4 h-4 text-primary" />}
            Create New Budget
            {!isPremium && (
              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                Premium
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {isPremium 
              ? 'Start a new trip budget while keeping your current trip data for reference.'
              : 'Upgrade to Premium to manage multiple trip budgets and keep your travel history organized.'}
          </p>
          <Button
            data-testid="create-new-budget-button"
            onClick={handleCreateNewBudget}
            className="w-full rounded-full bg-primary text-primary-foreground"
            disabled={!isPremium}
          >
            <Plus className="w-4 h-4 mr-2" />
            {isPremium ? 'Create New Budget' : 'Unlock with Premium'}
          </Button>
        </CardContent>
      </Card>

      {/* Theme Customization - Premium Only */}
      <Card data-testid="theme-customization-card" className={`bg-card border-border ${!isPremium ? 'opacity-90' : ''}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Theme & Layout
            {!isPremium && (
              <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                Premium
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {isPremium 
              ? 'Personalize your app with unique themes - Fantasy, Cyberpunk, Steampunk, and more!'
              : 'Upgrade to unlock custom themes including Fantasy Realm, Cyberpunk 2099, Steampunk Express, and more.'}
          </p>
          <Button
            data-testid="customize-theme-button"
            onClick={() => setShowThemeSelector(true)}
            className="w-full rounded-full bg-primary text-primary-foreground"
          >
            <Palette className="w-4 h-4 mr-2" />
            {isPremium ? 'Customize Theme' : 'Preview Themes'}
          </Button>
        </CardContent>
      </Card>

      {/* Budget Settings */}
      <Card data-testid="budget-settings-card" className="bg-card border-border">
        <CardHeader>
          <CardTitle>Budget Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="daily-budget">Daily Budget</Label>
            <div className="flex gap-2">
              <Input
                id="daily-budget"
                data-testid="daily-budget-input"
                type="number"
                placeholder="50"
                value={dailyBudget}
                onChange={(e) => setDailyBudget(e.target.value)}
                className="bg-secondary/50 border-transparent"
              />
              <Button data-testid="save-budget-button" onClick={saveDailyBudget} className="rounded-full">
                Save
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="currency">Default Currency</Label>
            <Select value={currency} onValueChange={saveCurrency}>
              <SelectTrigger data-testid="currency-select" id="currency" className="">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="USD">USD - United States</SelectItem>
                <SelectItem value="EUR">EUR - European Union</SelectItem>
                <SelectItem value="GBP">GBP - United Kingdom</SelectItem>
                <SelectItem value="JPY">JPY - Japan</SelectItem>
                <SelectItem value="AUD">AUD - Australia</SelectItem>
                <SelectItem value="CAD">CAD - Canada</SelectItem>
                <SelectItem value="CHF">CHF - Switzerland</SelectItem>
                <SelectItem value="CNY">CNY - China</SelectItem>
                <SelectItem value="SEK">SEK - Sweden</SelectItem>
                <SelectItem value="NZD">NZD - New Zealand</SelectItem>
                <SelectItem value="MXN">MXN - Mexico</SelectItem>
                <SelectItem value="SGD">SGD - Singapore</SelectItem>
                <SelectItem value="HKD">HKD - Hong Kong</SelectItem>
                <SelectItem value="NOK">NOK - Norway</SelectItem>
                <SelectItem value="KRW">KRW - South Korea</SelectItem>
                <SelectItem value="TRY">TRY - Turkey</SelectItem>
                <SelectItem value="INR">INR - India</SelectItem>
                <SelectItem value="RUB">RUB - Russia</SelectItem>
                <SelectItem value="BRL">BRL - Brazil</SelectItem>
                <SelectItem value="ZAR">ZAR - South Africa</SelectItem>
                <SelectItem value="THB">THB - Thailand</SelectItem>
                <SelectItem value="IDR">IDR - Indonesia</SelectItem>
                <SelectItem value="MYR">MYR - Malaysia</SelectItem>
                <SelectItem value="PHP">PHP - Philippines</SelectItem>
                <SelectItem value="VND">VND - Vietnam</SelectItem>
                <SelectItem value="CZK">CZK - Czech Republic</SelectItem>
                <SelectItem value="PLN">PLN - Poland</SelectItem>
                <SelectItem value="AED">AED - United Arab Emirates</SelectItem>
                <SelectItem value="ILS">ILS - Israel</SelectItem>
                <SelectItem value="ARS">ARS - Argentina</SelectItem>
                <SelectItem value="CLP">CLP - Chile</SelectItem>
                <SelectItem value="COP">COP - Colombia</SelectItem>
                <SelectItem value="PEN">PEN - Peru</SelectItem>
                <SelectItem value="EGP">EGP - Egypt</SelectItem>
                <SelectItem value="DKK">DKK - Denmark</SelectItem>
                <SelectItem value="HUF">HUF - Hungary</SelectItem>
                <SelectItem value="ISK">ISK - Iceland</SelectItem>
                <SelectItem value="MAD">MAD - Morocco</SelectItem>
                <SelectItem value="TWD">TWD - Taiwan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Account & Sign In - Hidden until referral program launches
      <Card data-testid="account-card" className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogIn className="w-5 h-5 text-primary" />
            Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {authUser ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {authUser.picture && (
                  <img src={authUser.picture} alt="" className="w-10 h-10 rounded-full" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">{authUser.name}</p>
                  <p className="text-sm text-muted-foreground truncate">{authUser.email}</p>
                </div>
              </div>
              <Button
                data-testid="sign-out-button"
                onClick={handleSignOut}
                variant="outline"
                className="w-full rounded-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Sign in to sync referrals, track free months, and keep your data across devices.
              </p>
              <Button
                data-testid="sign-in-button"
                onClick={handleSignIn}
                className="w-full rounded-full bg-primary text-primary-foreground"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Sign in with Google
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      */}

      {/* Referral Program - Coming Soon */}
      <Card data-testid="referral-card" className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
        <CardContent className="pt-6 pb-6 text-center space-y-3">
          <Gift className="w-10 h-10 text-amber-400 mx-auto" />
          <h3 className="text-xl font-bold text-foreground">Referral Program</h3>
          <p className="text-base text-muted-foreground">
            Earn <span className="text-amber-400 font-semibold">1 free month</span> of Premium for every friend who subscribes!
          </p>
          <button
            onClick={() => {
              const newTaps = adminTaps + 1;
              setAdminTaps(newTaps);
              if (newTaps >= 5) {
                setShowAdminInput(true);
                setAdminTaps(0);
              }
            }}
            className="inline-block bg-amber-500/20 border border-amber-500/40 rounded-full px-6 py-2.5"
            data-testid="referral-coming-soon"
          >
            <p className="text-amber-400 font-bold text-lg tracking-wide">Coming This Summer</p>
          </button>
          {showAdminInput && (
            <div className="flex gap-2 pt-2">
              <Input
                data-testid="admin-code-input"
                placeholder="Enter code"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value.toUpperCase())}
                className="bg-secondary/50 border-transparent font-mono text-center"
              />
              <Button
                data-testid="admin-code-submit"
                onClick={async () => {
                  const code = adminCode.trim().toUpperCase();
                  const MASTER_CODES = ['WANDERER', 'PREMIUM', 'TESTPRO', 'DEVMODE', 'MASTER-PREMIUM-2026'];
                  if (MASTER_CODES.includes(code)) {
                    await db.settings.put({ key: 'is_premium', value: true });
                    const until = new Date();
                    until.setFullYear(until.getFullYear() + 10);
                    await db.settings.put({ key: 'premium_until', value: until.toISOString() });
                    setIsPremium(true);
                    setShowAdminInput(false);
                    setAdminCode('');
                    toast.success('Premium unlocked!');
                  } else {
                    toast.error('Invalid code');
                  }
                }}
                className="rounded-full"
              >
                Go
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card data-testid="data-management-card" className="bg-card border-border">
        <CardHeader>
          <CardTitle>Data Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            data-testid="reset-trip-button"
            onClick={() => setShowResetModal(true)}
            variant="outline"
            className="w-full rounded-full border-primary text-primary hover:bg-primary/10"
          >
            Reset Trip Budget
          </Button>

          <div className="relative">
            {!isPremium && (
              <div className="absolute inset-0 bg-card/50 backdrop-blur-[2px] rounded-full flex items-center justify-center z-10">
                <Lock className="w-4 h-4 text-primary" />
              </div>
            )}
            <Button
              data-testid="export-data-button"
              onClick={isPremium ? exportData : () => setShowPaywall(true)}
              variant="outline"
              className="w-full rounded-full border-border relative"
              disabled={!isPremium}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data (JSON)
              {!isPremium && (
                <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                  Premium
                </span>
              )}
            </Button>
          </div>

          <Button
            data-testid="delete-all-data-button"
            onClick={deleteAllData}
            variant="destructive"
            className="w-full rounded-full"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All Data
          </Button>
        </CardContent>
      </Card>

      {/* Privacy Notice */}
      <Card className="bg-card border-border">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground text-center">
            Your data is stored locally on your device. We never sell your data.
            One-tap delete removes everything permanently.
          </p>
        </CardContent>
      </Card>

      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} />

      <OnboardingModal 
        open={showNewBudget} 
        onComplete={handleNewBudgetComplete}
      />

      <ResetTripModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={resetTrip}
      />

      <ThemeSelector
        open={showThemeSelector}
        onClose={() => setShowThemeSelector(false)}
        isPremium={isPremium}
        onUpgrade={() => {
          setShowThemeSelector(false);
          setShowPaywall(true);
        }}
      />
    </div>
  );
};

export default SettingsScreen;